import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Search, Mail, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SECTORS } from "@/lib/omSectors";
import { OmCard, OmHead, OmKpi, OmTable, Row, Td, Pill, GOLD, MUTED, NAVY, FIELD } from "@/components/om/OmBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useMeta } from "@/lib/useMeta";

const TONE = { New: "gold", Contacted: "warn", Qualified: "good", Converted: "good", "Not Interested": "muted" };
const when = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const SCALES = ["Below 10 Crore", "10 to 50 Crore", "50 to 200 Crore", "Above 200 Crore"];
const TIMELINES = ["Immediately", "Within 1 month", "Within 3 months", "Just exploring"];

export default function AdminLeads() {
  useMeta({ title: "Leads Desk | Plan-Sarthi", description: "Sector lead pipeline.", path: "/admin/leads" });
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [f, setF] = useState({ sector: "", status: "", scale: "", timeline: "", from: "", to: "" });

  const load = () => api.get("/leads").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => (data?.leads || []).filter((l) =>
    (!q || `${l.name} ${l.email} ${l.company}`.toLowerCase().includes(q.toLowerCase())) &&
    (!f.sector || l.sector === f.sector) && (!f.status || l.status === f.status) &&
    (!f.scale || l.project_scale === f.scale) && (!f.timeline || l.timeline === f.timeline) &&
    (!f.from || l.submitted_at >= f.from) && (!f.to || l.submitted_at <= `${f.to}T23:59:59`)), [data, q, f]);

  const update = async (patch) => {
    try {
      const { data: fresh } = await api.put(`/leads/${sel.id}`, patch);
      setSel(fresh); setNote(""); load();
      toast.success("Lead updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportXls = () => {
    const cols = ["name", "email", "phone", "company", "role", "sector", "project_type", "project_scale",
      "timeline", "status", "assigned_to", "source_page", "submitted_at", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "application/vnd.ms-excel" }));
    a.download = `PlanSarthi_Leads_${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
  };

  const sub = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const selectStyle = { ...FIELD, borderRadius: 6, height: 36, padding: "0 8px", fontSize: 12.5, border: "1px solid rgba(201,168,76,0.25)" };

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid="admin-leads-page">
      <div className="max-w-[1500px] mx-auto">
        <OmHead title="Leads Desk" subtitle="Every sector walkthrough request, with status and follow-up history.">
          <Link to="/admin" className="text-[12.5px] hover:underline" style={{ color: MUTED }} data-testid="leads-back-link">← Super Admin</Link>
          <Button variant="outline" onClick={exportXls} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="leads-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export Excel
          </Button>
        </OmHead>

        {!data ? <p className="text-[13px]" style={{ color: MUTED }}>Loading leads…</p> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <OmKpi label="Total leads" value={data.kpi.total} testid="kpi-leads-total" />
              <OmKpi label="New this week" value={data.kpi.new_this_week} testid="kpi-leads-week" />
              <OmKpi label="Contacted" value={data.kpi.contacted} />
              <OmKpi label="Qualified" value={data.kpi.qualified} />
              <OmKpi label="Converted" value={data.kpi.converted} />
              <OmKpi label="Conversion rate" value={`${data.kpi.conversion_rate}%`} testid="kpi-leads-conversion" />
            </div>

            <div className="flex flex-wrap gap-2 my-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company" className="pl-8 w-[230px] h-9" style={FIELD} data-testid="leads-search" />
              </div>
              <select value={f.sector} onChange={sub("sector")} style={selectStyle} data-testid="leads-sector-filter">
                <option value="">All sectors</option>
                {SECTORS.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
              </select>
              <select value={f.status} onChange={sub("status")} style={selectStyle} data-testid="leads-status-filter">
                <option value="">All statuses</option>
                {data.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={f.scale} onChange={sub("scale")} style={selectStyle} data-testid="leads-scale-filter">
                <option value="">All scales</option>
                {SCALES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={f.timeline} onChange={sub("timeline")} style={selectStyle} data-testid="leads-timeline-filter">
                <option value="">Any timeline</option>
                {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="date" value={f.from} onChange={sub("from")} style={selectStyle} data-testid="leads-from-filter" />
              <input type="date" value={f.to} onChange={sub("to")} style={selectStyle} data-testid="leads-to-filter" />
              <Pill tone="gold" testid="leads-count">{rows.length} of {data.leads.length}</Pill>
            </div>

            <OmCard>
              <OmTable head={["Name", "Company", "Role", "Sector", "Scale", "Timeline", "Submitted", "Status", "Assigned", ""]} testid="leads-table">
                {rows.map((l, i) => (
                  <Row key={l.id} i={i} testid={`lead-row-${l.id}`}>
                    <Td>{l.name}<div className="text-[10.5px]" style={{ color: MUTED }}>{l.email}</div></Td>
                    <Td>{l.company}</Td>
                    <Td style={{ color: MUTED }}>{l.role}</Td>
                    <Td>{l.sector}</Td>
                    <Td style={{ color: MUTED }}>{l.project_scale}</Td>
                    <Td style={{ color: MUTED }}>{l.timeline}</Td>
                    <Td className="tabular-nums" style={{ color: MUTED }}>{when(l.submitted_at)}</Td>
                    <Td><Pill tone={TONE[l.status]}>{l.status}</Pill></Td>
                    <Td style={{ color: MUTED }}>{l.assigned_to || "—"}</Td>
                    <Td><button onClick={() => setSel(l)} className="text-[11.5px] hover:underline" style={{ color: GOLD }} data-testid={`lead-open-${l.id}`}>View</button></Td>
                  </Row>
                ))}
                {rows.length === 0 ? <Row i={0}><Td className="text-[12.5px]">No leads match these filters.</Td></Row> : null}
              </OmTable>
            </OmCard>
          </>
        )}

        <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" style={{ background: "#112240", borderColor: `${GOLD}33` }} data-testid="lead-detail-drawer">
            <DialogHeader><DialogTitle className="text-white">{sel?.name} · {sel?.company}</DialogTitle></DialogHeader>
            {sel ? (
              <div className="space-y-3">
                <dl className="space-y-1.5 text-[12.5px]">
                  {[["Work email", sel.email], ["Phone", sel.phone], ["Role", sel.role], ["Sector", sel.sector],
                    ["Project type", sel.project_type], ["Project scale", sel.project_scale],
                    ["Timeline", sel.timeline], ["Message", sel.message], ["Source page", sel.source_page],
                    ["Submitted", when(sel.submitted_at)]].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4">
                      <dt style={{ color: MUTED }}>{k}</dt>
                      <dd className="text-white text-right break-all">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-[11px] uppercase mb-1.5" style={{ color: MUTED }}>Status</span>
                    <select value={sel.status} onChange={(e) => update({ status: e.target.value })} style={selectStyle} data-testid="lead-status-select">
                      {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase mb-1.5" style={{ color: MUTED }}>Assigned to</span>
                    <Input value={sel.assigned_to || ""} onChange={(e) => setSel({ ...sel, assigned_to: e.target.value })}
                      onBlur={(e) => update({ assigned_to: e.target.value })} className="h-9" style={FIELD} data-testid="lead-assigned-input" />
                  </label>
                </div>
                <div>
                  <span className="block text-[11px] uppercase mb-1.5" style={{ color: MUTED }}>Notes</span>
                  {(sel.notes || []).map((n, i) => (
                    <p key={i} className="text-[12px] text-white mb-1.5" data-testid="lead-note-row">
                      {n.text}<span className="block text-[10px]" style={{ color: MUTED }}>{n.by} · {when(n.at)}</span>
                    </p>
                  ))}
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Append a note" style={FIELD} data-testid="lead-note-input" />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => update({ note })} disabled={!note.trim()} className="font-semibold" style={{ background: GOLD, color: NAVY }} data-testid="lead-note-save-btn">Add note</Button>
                    <a href={`mailto:${sel.email}?subject=${encodeURIComponent(`Plan-Sarthi — your ${sel.sector} O&M walkthrough`)}&body=${encodeURIComponent(`Hello ${sel.name.split(" ")[0]},\n\nThank you for requesting a Plan-Sarthi O&M walkthrough for your ${sel.sector} project.\n\nWhen would suit you for a 20-minute session this week?\n\nRegards,\nPlan-Sarthi · Sarthi Associates`)}`}>
                      <Button size="sm" variant="outline" style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="lead-followup-btn">
                        <Mail className="w-3.5 h-3.5 mr-1.5" /> Send follow-up
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
